import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, Video, Trash2, Monitor } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useLang } from '@/context/LangContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProjectScoreCard } from '@/components/app/ProjectScoreCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import ProjectGantt from '@/components/app/ProjectGantt';
import ProjectBudget from '@/components/app/ProjectBudget';
import ProjectGED from '@/components/app/ProjectGED';
import ProjectAI from '@/components/app/ProjectAI';
import ProjectAudit from '@/components/app/ProjectAudit';
import ProjectExports from '@/components/app/ProjectExports';
import ProjectMeetings from '@/components/app/ProjectMeetings';
import ProjectActors from '@/components/app/ProjectActors';
import ProjectDetails from '@/components/app/ProjectDetails';
import ProjectDiscussion from '@/components/app/ProjectDiscussion';
import ExportProjectPreview from '@/components/app/ExportProjectPreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLang();
    const { session, profile } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [project, setProject] = useState(null);
    const [phases, setPhases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [confirmName, setConfirmName] = useState('');
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setLoading(true);
        const [{ data: p }, { data: ph }] = await Promise.all([
            supabase.from('projects').select('*').eq('id', id).maybeSingle(),
            supabase.from('project_phases').select('*').eq('project_id', id).order('position'),
        ]);
        setProject(p); setPhases(ph || []);
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const normalize = (s) => (s || '').replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();
    const canDelete = currentWorkspace?.is_admin || profile?.is_super_admin;
    const confirmDelete = async () => {
        if (normalize(confirmName) !== normalize(project?.name)) { toast.error('Le nom ne correspond pas'); return; }
        setDeleting(true);
        try {
            await axios.delete(`${API}/projects/${id}`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
                params: { workspace_id: currentWorkspace.id, confirm_name: normalize(project.name) },
            });
            toast.success('Projet déplacé dans la corbeille (récupérable 30 jours)');
            navigate('/app/projects');
        } catch (e) { toast.error(e.response?.data?.detail || e.message); setDeleting(false); }
    };

    const phaseLabels = useMemo(() => t('phases.label'), [t]);

    if (loading) return <div className="text-center py-20 text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14} /> {t('common.loading')}</div>;
    if (!project) return <div className="text-center py-20 text-foreground/60">404</div>;

    return (
        <div className="space-y-8">
            <div>
                <Link to="/app/projects" className="text-sm text-foreground/60 hover:text-primary inline-flex items-center mb-4">
                    <ArrowLeft size={14} className="mr-1" /> {t('common.back')}
                </Link>
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="eyebrow mb-2">{project.industry || '—'} · {project.code || project.id.slice(0, 8)}</div>
                        <h1 className="font-heading text-4xl">{project.name}</h1>
                        {project.description && <p className="text-foreground/70 mt-3 max-w-3xl">{project.description}</p>}
                    </div>
                    <div className="flex items-end gap-3 flex-wrap">
                        <div className="flex gap-3 flex-wrap">
                            {phases.slice(0, 6).map((ph) => (
                                <span key={ph.id} className="phase-badge bg-primary-light text-primary" data-testid={`phase-badge-${ph.phase}`}>
                                    {phaseLabels[ph.phase] || ph.title || ph.phase}
                                </span>
                            ))}
                        </div>
                        {canDelete && (
                            <Button variant="outline" size="sm"
                                className="border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => { setConfirmName(''); setDeleteOpen(true); }}
                                data-testid="project-delete-btn">
                                <Trash2 size={14} className="mr-1.5" /> Mettre à la corbeille
                            </Button>
                        )}
                        <Link to={`/app/projects/${id}/kiosk`}>
                            <Button variant="outline" size="sm" data-testid="project-kiosk-btn" title="Plein écran COPIL — rotation auto des écrans">
                                <Monitor size={14} className="mr-1.5" /> Mode kiosque
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl text-destructive">Mettre le projet à la corbeille</DialogTitle>
                        <DialogDescription>
                            Le projet sera <strong>déplacé dans la corbeille</strong> et restera récupérable
                            pendant <strong>30 jours</strong>. Les phases, tâches, dépenses, documents et réunions
                            sont conservés tant que le projet n'est pas purgé définitivement.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm">
                            Pour confirmer, saisissez exactement le nom du projet : <strong>{project.name}</strong>
                        </p>
                        <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                            placeholder={project.name} autoFocus data-testid="project-delete-confirm-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setDeleteOpen(false)}>Annuler</Button>
                        <Button variant="destructive" onClick={confirmDelete}
                            disabled={deleting || normalize(confirmName) !== normalize(project.name)}
                            data-testid="project-delete-confirm-btn">
                            {deleting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
                            Mettre à la corbeille
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="bg-surface-muted">
                    <TabsTrigger value="overview" data-testid="tab-overview">{t('projects.tabs.overview')}</TabsTrigger>
                    <TabsTrigger value="about" data-testid="tab-about">À propos</TabsTrigger>
                    <TabsTrigger value="actors" data-testid="tab-actors">Acteurs</TabsTrigger>
                    <TabsTrigger value="discussion" data-testid="tab-discussion">Discussion</TabsTrigger>
                    <TabsTrigger value="gantt" data-testid="tab-gantt">{t('projects.tabs.gantt')}</TabsTrigger>
                    <TabsTrigger value="budget" data-testid="tab-budget">{t('projects.tabs.budget')}</TabsTrigger>
                    <TabsTrigger value="ged" data-testid="tab-ged">{t('projects.tabs.ged')}</TabsTrigger>
                    <TabsTrigger value="ai" data-testid="tab-ai">{t('projects.tabs.ai')}</TabsTrigger>
                    <TabsTrigger value="meetings" data-testid="tab-meetings">Réunions</TabsTrigger>
                    <TabsTrigger value="exports" data-testid="tab-exports">Exports</TabsTrigger>
                    <TabsTrigger value="audit" data-testid="tab-audit">{t('projects.tabs.audit')}</TabsTrigger>
                </TabsList>

                <TabsContent value="about" className="mt-6"><ProjectDetails projectId={id} /></TabsContent>
                <TabsContent value="actors" className="mt-6"><ProjectActors projectId={id} /></TabsContent>
                <TabsContent value="discussion" className="mt-6"><ProjectDiscussion projectId={id} /></TabsContent>

                <TabsContent value="overview" className="mt-6">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        <div className="card-soft p-6"><div className="eyebrow">Budget</div><div className="font-heading text-2xl mt-2">{new Intl.NumberFormat('fr-FR').format(project.budget_total || 0)} {project.currency}</div></div>
                        <div className="card-soft p-6"><div className="eyebrow">Avancement</div><div className="font-heading text-2xl mt-2">{project.progress || 0}%</div></div>
                        <div className="card-soft p-6"><div className="eyebrow">Statut</div><div className="font-heading text-2xl mt-2 capitalize">{project.status}</div></div>
                        <div className="card-soft p-6"><div className="eyebrow">Pays</div><div className="font-heading text-2xl mt-2">{project.country || '—'}</div></div>
                    </div>
                    <div className="mt-6">
                        <ProjectScoreCard projectId={id} workspaceId={currentWorkspace?.id} />
                    </div>
                    <div className="card-soft p-7 mt-6">
                        <h3 className="font-heading text-2xl mb-4">{t('phases.milestones')}</h3>
                        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
                            {phases.map((ph) => (
                                <div key={ph.id} className="border border-border rounded-lg p-4">
                                    <div className="eyebrow text-secondary">{phaseLabels[ph.phase] || ph.title || ph.phase}</div>
                                    <div className="font-medium mt-2 text-sm">{ph.title}</div>
                                    <div className="text-xs text-muted-foreground mt-1">{ph.progress || 0}%</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="gantt" className="mt-6"><ProjectGantt projectId={id} workspaceId={currentWorkspace?.id} phases={phases} /></TabsContent>
                <TabsContent value="budget" className="mt-6"><ProjectBudget projectId={id} workspaceId={currentWorkspace?.id} /></TabsContent>
                <TabsContent value="ged" className="mt-6"><ProjectGED projectId={id} workspaceId={currentWorkspace?.id} /></TabsContent>
                <TabsContent value="ai" className="mt-6"><ProjectAI projectId={id} project={project} /></TabsContent>
                <TabsContent value="meetings" className="mt-6"><ProjectMeetings projectId={id} /></TabsContent>
                <TabsContent value="exports" className="mt-6"><ProjectExports project={project} workspaceId={currentWorkspace?.id} /></TabsContent>
                <TabsContent value="audit" className="mt-6"><ProjectAudit projectId={id} workspaceId={currentWorkspace?.id} /></TabsContent>
            </Tabs>

            {/* Floating action button — démarrer une réunion depuis n'importe quel onglet */}
            {activeTab !== 'meetings' && (
                <button
                    onClick={() => setActiveTab('meetings')}
                    className="fixed bottom-24 right-6 z-[60] h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary-hover hover:scale-105 transition-all flex items-center justify-center group"
                    title="Démarrer / rejoindre une réunion"
                    aria-label="Réunions du projet"
                    data-testid="project-meetings-fab"
                >
                    <Video size={22} strokeWidth={1.8} />
                    <span className="absolute right-full mr-3 bg-foreground text-background text-xs px-2.5 py-1.5 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        Réunions du projet
                    </span>
                </button>
            )}
        </div>
    );
}
